// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { RemakePlantSelection } from "src/app/modules/properties/mapping-archive-docs.utils";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class CondoStructuresService {
  /**
   * Class constructor
   */
  remakeStrucutureDocs: any = {};

  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) {}

  /**
   * Set docs of remake structure
   *
   * @since 1.0.0
   *
   * @returns void
   */
  setRemakeStrucutureDocs(
    activeSection: string,
    docId: string,
    subDoc: any
  ): void {
    this.remakeStrucutureDocs = {
      activeSection,
      docId,
      subDoc,
    };
  }

  /**
   * Get docs of remake structure
   *
   * @since 1.0.0
   *
   * @returns void
   */
  getRemakeStrucutureDocs(): any {
    return this.remakeStrucutureDocs;
  }

  /**
   * Set the documents of a remake on the 3.0.0 archive (MAP-A1): the element being remade and the
   * ticked rows of the new archive, by id.
   *
   * Same slot as @see setRemakeStrucutureDocs, so what reads it does not change: the mapping tab
   * matches `activeSection` against the section of the new element and sends the whole object as
   * `remakePlantDocs` on the POST that creates it, where the back-end reads `sourceStructureId` and
   * `documentArchiveDocIds` (and ignores `activeSection`). Without `docId` and `subDoc` the legacy
   * copy does not run: the rows go to the new archive only.
   *
   * @since 3.0.0
   *
   * @param activeSection Section of the element being remade, where the new one is created
   * @param selection     The element being remade and the ticked rows
   */
  setRemakePlantSelection(activeSection: string, selection: RemakePlantSelection): void {
    this.remakeStrucutureDocs = {
      activeSection,
      sourceStructureId: selection.sourceStructureId,
      documentArchiveDocIds: selection.documentArchiveDocIds,
    };
  }

  /**
   * Create a new condo structure
   *
   * @since 1.0.0
   *
   * @param condoStructure condoStructure data
   * @returns Observable<any>
   */
  create(propertyId: string): Observable<any> {
    const data = {
      property: propertyId,
    };
    return this.apiService.apiCall(
      "POST",
      "condo-structures",
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get condo structures list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      "condo-structures",
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get a list of all available structures (connection) in given condoStructureId
   *
   * @since 1.0.0
   */
  getCondoStructuresListForSelect(condoStructureId: string): Observable<any> {
    return this.apiService.apiCall(
      "GET",
      `condo-structures/${condoStructureId}/connections`,
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get a list of all available electric meters (connection) in given condoStructureId
   *
   * @since 1.0.0
   */
  getCondoStructuresListForElectricMeter(
    condoStructureId: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "GET",
      `condo-structures/${condoStructureId}/electric-meter`,
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get a list of all available water meters (connection) in given condoStructureId
   *
   * @since 1.0.0
   */
  getCondoStructuresListForWaterMeter(
    condoStructureId: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "GET",
      `condo-structures/${condoStructureId}/water-meter`,
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get a list of all available gas meters (connection) in given condoStructureId
   *
   * @since 1.0.0
   */
  getCondoStructuresListForGasMeter(condoStructureId: string): Observable<any> {
    return this.apiService.apiCall(
      "GET",
      `condo-structures/${condoStructureId}/gas-meter`,
      {},
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get a list of all available Caretakers and Cleaning Companies in given condoStructureId
   *
   * @since 1.0.0
   */
  getCondoStructuresCaretakers(condoStructureId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `condo-structures/${condoStructureId}/caretakers`,
        {},
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "condo-structures/export",
        {},
        { responseType: "blob" },
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given condo structure with condoStructure ID
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of the condoStructure to get
   */
  get(condoStructureId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `condo-structures/${condoStructureId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get given condo structure with condoStructure ID
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of the condoStructure to get
   */
  getView(condoStructureId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `condo-structures/${condoStructureId}/view`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get given condo structure with condoStructure ID per QRcode area
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of the condoStructure to get
   */
  getQrcode(condoStructureId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `condo-structures/qrcode/${condoStructureId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }
  /**
   * Update condo structure with given condoStructureId with given data
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of condo structure to update
   * @param data New condo structure's data
   */
  update(condoStructureId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `condo-structures/${condoStructureId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete permanently a condo structure
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of the condo structure
   */
  delete(condoStructureId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `condo-structures/${condoStructureId}`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update condo structure section
   *
   * @since 1.0.0
   *
   * @param endpoint Section raw endpoint
   * @param condoStructureId ID of condo structure to update
   * @param sectionId ID of condo structure section to update
   * @param data New condo structure's data
   */
  updateSection(
    endpoint: string,
    condoStructureId: string,
    sectionId: string,
    data: object,
    mappaturaOpen: boolean
  ): Observable<any> {
    const endpoint2 = endpoint.replace(":structureId", condoStructureId);
    if (endpoint === "condo-structures/:structureId/anagrafica") {
      const endpoint3 = endpoint2.replace("/anagrafica", "");
      return this.apiService.apiCall(
        "PATCH",
        `${endpoint3}`,
        { open: mappaturaOpen, anagrafica: data },
        undefined,
        undefined,
        undefined,
        "v2"
      );
    }
    return this.apiService.apiCall(
      "PUT",
      `${endpoint2}/${sectionId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Create condo structure section item
   *
   * @since 1.0.0
   *
   * @param endpoint Section raw endpoint
   * @param condoStructureId ID of condo structure where to create section item
   * @param data New condo structure item's data
   */
  createSectionItem(
    endpoint: string,
    condoStructureId: string,
    data: object
  ): Observable<any> {
    const endpoint2 = endpoint.replace(":structureId", condoStructureId);
    return this.apiService.apiCall(
      "POST",
      endpoint2,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update condo structure section
   *
   * @since 1.0.0
   *
   * @param endpoint Section raw endpoint
   * @param condoStructureId ID of condo structure to update
   * @param sectionId ID of condo structure section to update
   * @param data New condo structure's data
   */
  deleteSection(
    endpoint: string,
    condoStructureId: string,
    sectionId: string
  ): Observable<any> {
    const endpoint2 = endpoint.replace(":structureId", condoStructureId);
    return this.apiService.apiCall(
      "DELETE",
      `${endpoint2}/${sectionId}`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update condo structure section
   *
   * @since 1.0.0
   *
   * @param text Note text
   * @param condoStructureId ID of condo structure to update
   * @param structureName Name of condo structure section
   * @param structureId ID of condo structure section to update
   * @param noteId New condo structure's note to delete
   */
  updateNote(
    data: object,
    condoStructureId: string,
    structureName: string,
    structureId: string,
    noteId: string
  ): Observable<any> {
    const structureNameEncoded = encodeURIComponent(structureName);
    return this.apiService.apiCall(
      "PUT",
      `condo-structures/${condoStructureId}/note/${structureNameEncoded}/${structureId}/${noteId}/`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update condo structure section
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of condo structure to update
   * @param structureName Name of condo structure section
   * @param structureId ID of condo structure section to update
   * @param noteId New condo structure's note to delete
   */
  deleteNote(
    condoStructureId: string,
    structureName: string,
    structureId: string,
    noteId: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `condo-structures/${condoStructureId}/note/${structureId}/${noteId}/`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
}
